/* eslint-disable prefer-const */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Button,
  Card,
  CardContent,
  Container,
  Link,
  Grid,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Box,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
//Models
import type { ResetPasswordRequest } from "../../services/model/resetPasswordRequest";
import { resetPassword } from "../../services/api/user-service";
import { useConfirm } from "@/components/dialog/confirm";
import EnvironmentFlag from "@/components/environmentFlag";
import BrandLoader from "@/branding/BrandLoader";
import BrandLogo from "@/branding/BrandLogo";

interface ResetPasswordProps {
  style?: any;
  loading: boolean;
}

const ResetPassword: React.FC<ResetPasswordProps> = (_props) => {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { tenant } = useParams<{ tenant: string }>();
  let [loading, setLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get("token");
  const email = searchParams.get("email");

  const showDialog = (message: string, success: boolean) => {
    let title: string = "Registration";
    if (success === false) title = "Registration Failed";
    confirm({
      catchOnCancel: false,
      title: title,
      description: message,
    }).then(() => routeToLogin(success));
  };

  const routeToLogin = (success: boolean) => {
    if (success) {
      navigate(tenant ? `/${tenant}/login` : "/login");
    }
  };

  // At least 12 chars, one lowercase, one uppercase, one digit, one non-alphanumeric
  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{12,}$/;

  return (
    <div>
      <Formik
        initialValues={{
          password: "",
          confirmPassword: "",
          showPassword: false,
          showConfirmPassword: false,
        }}
        validationSchema={Yup.object().shape({
          password: Yup.string()
            .matches(
              passwordRegex,
              "Password must be at least 12 characters and include at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character"
            )
            .required("Password is required"),
          confirmPassword: Yup.string()
            .oneOf([Yup.ref("password")], "Passwords must match")
            .required("Confirm Password is required"),
        })}
        onSubmit={async (fields) => {
          try {
            setLoading(true);
            if (fields) {
              const register: ResetPasswordRequest = {
                token: resetToken as string,
                email: email as string,
                newPassword: fields.password,
                isApp: true,
                appTenantName: tenant,
              };
              const response = await resetPassword(register);
              if (response) {
                setLoading(false);
                showDialog(response.message, response.resultCode);
              } else {
                setLoading(false);
                showDialog(
                  "There seems to be a problem with your internet connection. Please check your connection and try again.",
                  false
                );
              }
            }
          } catch (err) {
            setLoading(false);
            showDialog(
              "An exception occurred, please contact your administrator.",
              false
            );
            if (err instanceof Error) {
              console.error(err.message);
            }
          }
        }}
      >
        {(props) => {
          const {
            values,
            setValues,
            touched,
            errors,
            handleChange,
            handleBlur,
          } = props;

          return (
            <>
              <Container maxWidth="sm">
                <Grid
                  container
                  spacing={2}
                  justifyContent="center"
                  direction="column"
                >
                  <Card variant="outlined">
                    <CardContent>
                      <EnvironmentFlag />
                      <Grid container justifyContent="center">
                        <BrandLogo size={150} shape="circle" ring shadow />
                      </Grid>
                      <Box sx={{ height: 60 }} />
                      <Grid container justifyContent="center">
                        <Typography align="center" variant="subtitle1">
                          Enter your new password below
                        </Typography>
                      </Grid>
                      <Grid size={12}>
                        <br />
                      </Grid>
                      <Grid size={12}>
                        <Form noValidate>
                          <Grid container spacing={2}>
                            <Grid size={12}>
                              <TextField
                                type={values.showPassword ? "text" : "password"}
                                label="Password"
                                name="password"
                                value={values.password}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                                variant="outlined"
                                required
                                error={!!(errors.password && touched.password)}
                                helperText={touched.password && errors.password}
                                slotProps={{
                                  input: {
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          onClick={() =>
                                            setValues({
                                              ...values,
                                              showPassword:
                                                !values.showPassword,
                                            })
                                          }
                                          aria-label="toggle password visibility"
                                          edge="end"
                                        >
                                          {values.showPassword ? (
                                            <Visibility />
                                          ) : (
                                            <VisibilityOff />
                                          )}
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  },
                                }}
                              />
                            </Grid>
                            <Grid size={12}>
                              <TextField
                                type={
                                  values.showConfirmPassword
                                    ? "text"
                                    : "password"
                                }
                                label="Confirm Password"
                                name="confirmPassword"
                                value={values.confirmPassword}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                                variant="outlined"
                                required
                                error={
                                  !!(
                                    errors.confirmPassword &&
                                    touched.confirmPassword
                                  )
                                }
                                helperText={
                                  touched.confirmPassword &&
                                  errors.confirmPassword
                                }
                                slotProps={{
                                  input: {
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          onClick={() =>
                                            setValues({
                                              ...values,
                                              showConfirmPassword:
                                                !values.showConfirmPassword,
                                            })
                                          }
                                          aria-label="toggle password visibility"
                                          edge="end"
                                        >
                                          {values.showConfirmPassword ? (
                                            <Visibility />
                                          ) : (
                                            <VisibilityOff />
                                          )}
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  },
                                }}
                              />
                            </Grid>
                            <Grid size={12}>
                              <br />
                              <Button
                                type="submit"
                                value="ForgotPassword"
                                variant="contained"
                                color="secondary"
                                fullWidth
                                size="large"
                              >
                                <strong>Submit</strong>
                              </Button>
                            </Grid>
                          </Grid>
                        </Form>
                      </Grid>
                      <Grid size={12}>
                        <br />
                        <Grid
                          container
                          justifyContent="center"
                          alignItems="center"
                          direction="column"
                        >
                          <Typography>
                            <Link
                              component="button"
                              onClick={() => routeToLogin(true)}
                              underline="always"
                              color="primary"
                            >
                              <strong>Go back to Login - Click Here</strong>
                            </Link>
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Container>
            </>
          );
        }}
      </Formik>
      <BrandLoader open={loading} />
    </div>
  );
};

export default ResetPassword;
